package br.com.vvrefeicoes.portal.feature.audit

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SegmentedButton
import androidx.compose.material3.SegmentedButtonDefaults
import androidx.compose.material3.SingleChoiceSegmentedButtonRow
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppBadge
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppFooter
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.AppPagination
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.AppTooltipText
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.layout.AppScrollColumn
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.domain.model.AuditEntry
import br.com.vvrefeicoes.portal.domain.model.AuditResult
import com.composables.icons.lucide.Download
import com.composables.icons.lucide.EllipsisVertical
import com.composables.icons.lucide.Eye
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Search
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.audit_action_details
import portalvv.shared.generated.resources.audit_apply
import portalvv.shared.generated.resources.audit_cancel
import portalvv.shared.generated.resources.audit_col_actions
import portalvv.shared.generated.resources.audit_col_code
import portalvv.shared.generated.resources.audit_col_date
import portalvv.shared.generated.resources.audit_col_description
import portalvv.shared.generated.resources.audit_col_id
import portalvv.shared.generated.resources.audit_col_module
import portalvv.shared.generated.resources.audit_col_result
import portalvv.shared.generated.resources.audit_col_user
import portalvv.shared.generated.resources.audit_custom_end
import portalvv.shared.generated.resources.audit_custom_start
import portalvv.shared.generated.resources.audit_custom_title
import portalvv.shared.generated.resources.audit_date_placeholder
import portalvv.shared.generated.resources.audit_details_title
import portalvv.shared.generated.resources.audit_empty
import portalvv.shared.generated.resources.audit_export
import portalvv.shared.generated.resources.audit_filter_all
import portalvv.shared.generated.resources.audit_filter_custom
import portalvv.shared.generated.resources.audit_filter_month
import portalvv.shared.generated.resources.audit_filter_week
import portalvv.shared.generated.resources.audit_invalid_date_range
import portalvv.shared.generated.resources.audit_result_failure
import portalvv.shared.generated.resources.audit_result_none
import portalvv.shared.generated.resources.audit_result_success
import portalvv.shared.generated.resources.audit_retry
import portalvv.shared.generated.resources.audit_search_placeholder
import portalvv.shared.generated.resources.audit_title

private val WeightDate = 1.1f
private val WeightId = 0.6f
private val WeightCode = 1.2f
private val WeightUser = 1.4f
private val WeightModule = 1.1f
private val WeightDescription = 3f
private val WeightResult = 0.9f
private val WeightActions = 0.5f

@Composable
fun AuditScreen() {
    val controller = rememberAuditController()

    LaunchedEffect(controller) {
        controller.init()
    }

    AppScrollColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(AppSpacing.space24),
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        footer = { AppFooter() },
    ) {
        Text(
            text = stringResource(Res.string.audit_title),
            style = MaterialTheme.typography.headlineMedium,
        )

        controller.errorMessage?.let { message ->
            Surface(
                modifier = Modifier.fillMaxWidth(),
                color = MaterialTheme.colorScheme.errorContainer,
                shape = AppShapes.medium,
            ) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(AppSpacing.space12),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
                ) {
                    Text(
                        text = message,
                        color = MaterialTheme.colorScheme.onErrorContainer,
                        modifier = Modifier.weight(1f),
                    )
                    AppButton(
                        text = stringResource(Res.string.audit_retry),
                        onClick = controller::retry,
                        variant = PortalButtonVariant.Outlined,
                    )
                }
            }
        }

        FiltersCard(controller)

        if (controller.loading) {
            Box(
                modifier = Modifier.fillMaxWidth().height(240.dp),
                contentAlignment = Alignment.Center,
            ) {
                CircularProgressIndicator()
            }
        } else {
            AuditTable(controller)
            AppPagination(
                page = controller.page,
                pageCount = controller.pageCount,
                onPageChange = controller::goToPage,
                modifier = Modifier.align(Alignment.End),
            )
        }
    }

    if (controller.customOpen) {
        CustomRangeDialog(controller)
    }

    controller.detailsEntry?.let { entry ->
        AuditDetailsDialog(
            entry = entry,
            loading = controller.detailsLoading,
            error = controller.detailsError,
            onRetry = controller::retryDetails,
            onDismiss = controller::dismissDetails,
        )
    }
}

@Composable
private fun FiltersCard(controller: AuditController) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space16),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space16),
            ) {
                AppTextField(
                    value = controller.query,
                    onValueChange = controller::updateQuery,
                    placeholder = stringResource(Res.string.audit_search_placeholder),
                    leadingIcon = { Icon(Lucide.Search, contentDescription = null) },
                    modifier = Modifier.weight(1f),
                )
                DateFilterSegmented(selected = controller.dateFilter, onSelect = controller::updateDateFilter)
            }
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8, Alignment.End),
            ) {
                AppButton(
                    text = stringResource(Res.string.audit_export),
                    onClick = {},
                    variant = PortalButtonVariant.Outlined,
                    leadingIcon = Lucide.Download,
                )
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun DateFilterSegmented(
    selected: AuditDateFilter,
    onSelect: (AuditDateFilter) -> Unit,
) {
    val options = AuditDateFilter.entries
    SingleChoiceSegmentedButtonRow {
        options.forEachIndexed { index, option ->
            SegmentedButton(
                selected = selected == option,
                onClick = { onSelect(option) },
                shape = SegmentedButtonDefaults.itemShape(index = index, count = options.size),
            ) {
                Text(
                    text = option.label(),
                    style = MaterialTheme.typography.labelLarge,
                )
            }
        }
    }
}

@Composable
private fun AuditTable(controller: AuditController) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            AuditHeaderRow()
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            if (controller.paged.isEmpty()) {
                EmptyState(message = stringResource(Res.string.audit_empty))
            } else {
                controller.paged.forEachIndexed { index, item ->
                    AuditRow(item = item, controller = controller)
                    if (index < controller.paged.lastIndex) {
                        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                    }
                }
            }
        }
    }
}

@Composable
private fun AuditHeaderRow() {
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = AppSpacing.space16, vertical = AppSpacing.space12),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
    ) {
        HeaderCell(stringResource(Res.string.audit_col_id), WeightId)
        HeaderCell(stringResource(Res.string.audit_col_date), WeightDate)
        HeaderCell(stringResource(Res.string.audit_col_code), WeightCode)
        HeaderCell(stringResource(Res.string.audit_col_user), WeightUser)
        HeaderCell(stringResource(Res.string.audit_col_module), WeightModule)
        HeaderCell(stringResource(Res.string.audit_col_description), WeightDescription)
        HeaderCell(stringResource(Res.string.audit_col_result), WeightResult)
        HeaderCell(stringResource(Res.string.audit_col_actions), WeightActions)
    }
}

@Composable
private fun RowScope.HeaderCell(
    text: String,
    weight: Float,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        modifier = Modifier.weight(weight),
    )
}

@Composable
private fun AuditRow(
    item: AuditEntry,
    controller: AuditController,
) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(horizontal = AppSpacing.space16, vertical = AppSpacing.space12),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
    ) {
        BodyCell("#${item.id}", WeightId)
        BodyCell(dateLabel(item.occurredAt), WeightDate)
        BodyCell(item.code, WeightCode)
        BodyCell(item.userName, WeightUser)
        BodyCell(item.module, WeightModule)
        BodyCell(item.description, WeightDescription)
        Box(modifier = Modifier.weight(WeightResult)) {
            ResultBadge(item.result)
        }
        Box(modifier = Modifier.weight(WeightActions)) {
            AuditActionsMenu(entry = item, controller = controller)
        }
    }
}

@Composable
private fun AuditActionsMenu(
    entry: AuditEntry,
    controller: AuditController,
) {
    var expanded by remember { mutableStateOf(false) }
    Box {
        IconButton(
            onClick = { expanded = true },
            modifier = Modifier.size(24.dp),
        ) {
            Icon(
                imageVector = Lucide.EllipsisVertical,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        DropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }) {
            DropdownMenuItem(
                text = { Text(stringResource(Res.string.audit_action_details)) },
                leadingIcon = { Icon(Lucide.Eye, contentDescription = null) },
                onClick = {
                    expanded = false
                    controller.openDetails(entry)
                },
            )
        }
    }
}

@Composable
private fun RowScope.BodyCell(
    text: String,
    weight: Float,
) {
    AppTooltipText(
        text = text,
        style = MaterialTheme.typography.bodyMedium,
        color = MaterialTheme.colorScheme.onSurface,
        modifier = Modifier.weight(weight),
    )
}

@Composable
private fun ResultBadge(result: AuditResult) {
    val colors = result.badgeColors()
    AppBadge(
        text = result.label(),
        containerColor = colors.first,
        contentColor = colors.second,
    )
}

@Composable
private fun AuditDetailsDialog(
    entry: AuditEntry,
    loading: Boolean,
    error: String?,
    onRetry: () -> Unit,
    onDismiss: () -> Unit,
) {
    AppModalCard(
        title = stringResource(Res.string.audit_details_title),
        onDismiss = onDismiss,
    ) {
        if (loading) {
            Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
                CircularProgressIndicator()
            }
        } else if (error != null) {
            Text(error, color = MaterialTheme.colorScheme.error)
            AppButton(text = stringResource(Res.string.audit_retry), onClick = onRetry)
        } else {
            DetailRow(stringResource(Res.string.audit_col_id), "#${entry.id}")
            DetailRow(stringResource(Res.string.audit_col_date), dateLabel(entry.occurredAt))
            DetailRow(stringResource(Res.string.audit_col_code), entry.code)
            DetailRow(stringResource(Res.string.audit_col_user), entry.userName)
            DetailRow(stringResource(Res.string.audit_col_module), entry.module)
            DetailRow(stringResource(Res.string.audit_col_description), entry.description)
            DetailRow(stringResource(Res.string.audit_col_result), entry.result.label())
        }
    }
}

@Composable
private fun DetailRow(
    label: String,
    value: String,
) {
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurface,
        )
    }
}

@Composable
private fun CustomRangeDialog(controller: AuditController) {
    var start by remember { mutableStateOf(controller.customStart) }
    var end by remember { mutableStateOf(controller.customEnd) }
    AppModalCard(
        title = stringResource(Res.string.audit_custom_title),
        onDismiss = controller::dismissCustom,
    ) {
        AppTextField(
            value = start,
            onValueChange = { start = it },
            label = stringResource(Res.string.audit_custom_start),
            placeholder = stringResource(Res.string.audit_date_placeholder),
            modifier = Modifier.fillMaxWidth(),
        )
        controller.customError?.let {
            Text(
                stringResource(Res.string.audit_invalid_date_range),
                color = MaterialTheme.colorScheme.error,
            )
        }
        AppTextField(
            value = end,
            onValueChange = { end = it },
            label = stringResource(Res.string.audit_custom_end),
            placeholder = stringResource(Res.string.audit_date_placeholder),
            modifier = Modifier.fillMaxWidth(),
        )
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8, Alignment.End),
        ) {
            AppButton(
                text = stringResource(Res.string.audit_cancel),
                onClick = controller::dismissCustom,
                variant = PortalButtonVariant.Text,
            )
            AppButton(
                text = stringResource(Res.string.audit_apply),
                onClick = { controller.applyCustom(start, end) },
                variant = PortalButtonVariant.Filled,
            )
        }
    }
}

@Composable
private fun AuditDateFilter.label(): String =
    when (this) {
        AuditDateFilter.WEEK -> stringResource(Res.string.audit_filter_week)
        AuditDateFilter.MONTH -> stringResource(Res.string.audit_filter_month)
        AuditDateFilter.ALL -> stringResource(Res.string.audit_filter_all)
        AuditDateFilter.CUSTOM -> stringResource(Res.string.audit_filter_custom)
    }

@Composable
private fun AuditResult.label(): String =
    when (this) {
        AuditResult.SUCCESS -> stringResource(Res.string.audit_result_success)
        AuditResult.FAILURE -> stringResource(Res.string.audit_result_failure)
        AuditResult.NONE -> stringResource(Res.string.audit_result_none)
    }

private fun AuditResult.badgeColors(): Pair<Color, Color> =
    when (this) {
        AuditResult.SUCCESS -> AppPalette.Success100 to AppPalette.Success900
        AuditResult.FAILURE -> AppPalette.Error100 to AppPalette.Error900
        AuditResult.NONE -> AppPalette.Neutral200 to AppPalette.Neutral900
    }

private fun dateLabel(iso: String): String {
    if (iso.length < 16) return iso
    val date = iso.substring(0, 10)
    val time = iso.substring(11, 16)
    return "${date.substring(8, 10)}/${date.substring(5, 7)}/${date.substring(0, 4)} às $time"
}
